import { Component, Inject, OnInit, OnDestroy, Optional, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OtpDialogComponent, OtpDialogData } from '../../../../shared/components/otp-dialog/otp-dialog.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Facility } from '../facility-maintenance.component';
import { ReferralGroup, REFERRAL_GROUPS, ReferralGroupService } from '../../../../shared/constants/referral-groups.constants';
import { FacilityService } from 'app/shared/services/facility.service';
import { AlertService } from 'app/shared/services/alert/alert.service';
import { MatSelect } from '@angular/material/select';
import { Location } from '@angular/common';
import { JwtAuthService } from 'app/shared/services/auth/jwt-auth.service';

export interface PracticeSoftware {
  id: string;
  name: string;
  image?: string;
}

export interface FacilityDialogData {
  facility?: Facility | null;
  id?: string;
  ClinicName?: string;
  FacilityID?: string;
  practiceSoftware?: string;
  referralGroups: ReferralGroup[];
  isEdit: boolean;
  isRoleBasedUser: boolean;
  userReferralGroupId: number | null;
  userReferralGroupName?: string | null;
  isStandalonePage?: boolean;
  streetAddress?: string;
  city?: string;
  state?: string;
  postcode?: string;
  practiceManagerName?: string;
  practiceManagerEmail?: string;
  practiceManagerMobile?: string;
  itTechnicalName?: string;
  itTechnicalEmail?: string;
  itTechnicalMobile?: string;
  referralGroupRequesterName?: string;
  referralGroupRequesterEmail?: string;
  referralGroupRequesterPhone?: string;
  agreements_at?: string | null;
  notes?: string | null;
  accountManagerName?: string | null;
}

@Component({
    selector: 'app-facility-add-dialog',
    templateUrl: './facility-add-dialog.component.html',
    styleUrls: ['./facility-add-dialog.component.scss'],
    standalone: false
})
export class FacilityAddDialogComponent implements OnInit, OnDestroy {
  facilityForm: UntypedFormGroup;
  isSubmitting = false;
  selectedReferralGroupName: string = '';
  isStandalonePage: boolean = false;
  isStandaloneAgreement: boolean = false;
  facilityHash: string | null = null;

  practiceSoftwareOptions: PracticeSoftware[] = [
    { id: 'Medical Director', name: 'Medical Director', image: 'assets/images/logos/medical-director.png' },
    { id: 'Best Practice', name: 'Best Practice', image: 'assets/images/logos/best-practice.png' },
    { id: 'custom', name: 'Other (please specify)' }
  ];

  private destroy$ = new Subject<void>();
  public otpDialogRef: any = null;

  showAgreementView = false;
  facilityData: any = null;
  agreementForm: UntypedFormGroup;

  agreements = {};

  performerIdsOptions: any[] = [];
  performerIdsPage = 1;
  performerIdsTotal = 0;
  isLoadingPerformerIds = false;
  performerIdsNextPageUrl: string | null = null;

  @ViewChild('performerIdsSelect') performerIdsSelect!: MatSelect;

  constructor(
    private fb: UntypedFormBuilder,
    private facilityService: FacilityService,
    private alertService: AlertService,
    @Optional() private dialogRef: MatDialogRef<FacilityAddDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: FacilityDialogData,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private referralGroupService: ReferralGroupService,
    private location: Location,
    private jwtAuthService: JwtAuthService
  ) {
    const routeData = this.route.snapshot.data;
    this.facilityHash = this.route.snapshot.paramMap.get('hash');
    this.isStandaloneAgreement = routeData['isStandalonePageAgreement'] || false;

    if (!this.data) {
      this.data = {
        referralGroups: REFERRAL_GROUPS,
        isEdit: routeData['isEdit'] || false,
        isRoleBasedUser: routeData['isStandalonePage'] ? false : true,
        userReferralGroupId: null,
        isStandalonePage: routeData['isStandalonePage'] || false,
        ClinicName: '',
        FacilityID: '',
        practiceSoftware: ''
      };
    } else if (!this.data.referralGroups || this.data.referralGroups.length === 0) {
      this.data.referralGroups = REFERRAL_GROUPS;
    }
    
    this.isStandalonePage = this.data.isStandalonePage || false;
    this.facilityForm = this.createForm();

    this.agreementForm = this.fb.group({
      agreePrivacyPolicy: [false, Validators.requiredTrue],
      agreeMedmateTerms: [false, Validators.requiredTrue],
      agreeEula: [false, Validators.requiredTrue]
    });
  }

  ngOnInit() {    
    if (this.isStandaloneAgreement && this.facilityHash) {
      this.loadFacilityByHash(this.facilityHash);
    } else if (this.data.facility && this.data.isEdit) {
      this.populateForm();
    }

    if ((this.isStandalonePage || this.isStandaloneAgreement) && this.data.isEdit) {
      this.facilityForm.get('facilityId')?.disable();
    }

    var selectedReferralGroupId = this.referralGroupService.getSelectedReferralGroup();
    if(selectedReferralGroupId) {
      this.data.userReferralGroupId = selectedReferralGroupId;
      this.facilityForm.get('referralGroup')?.setValue(selectedReferralGroupId);
      this.facilityForm.get('referralGroup')?.disable();
    } else if(this.isStandaloneAgreement && this.facilityHash) this.location.back();

    if (this.data.userReferralGroupId && this.data.referralGroups) {
      const selectedGroup = this.data.referralGroups.find(
        group => group.value === this.data.userReferralGroupId
      );
      this.selectedReferralGroupName = selectedGroup?.label || '';
    }

    this.facilityForm.get('referralGroup')?.valueChanges.subscribe(referralGroupId => {
      if (referralGroupId) {
        this.performerIdsOptions = [];
        this.performerIdsPage = 1;
        this.loadPerformerIds(referralGroupId, 1);
      } else {
        this.performerIdsOptions = [];
      }
    });

    const referralGroupId = this.facilityForm.get('referralGroup')?.value;
    if (referralGroupId) {
      this.performerIdsOptions = [];
      this.performerIdsPage = 1;
      this.loadPerformerIds(referralGroupId, 1);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.otpDialogRef) this.otpDialogRef.close();
  }

  createForm(): UntypedFormGroup {
    const baseForm = {
      facilityId: ['', [this.data.isEdit ? Validators.required : Validators.nullValidator]],
      facilityName: ['', [Validators.required]],
      streetAddress: ['', [Validators.required]],
      city: ['',[Validators.required]],  
      state: ['',[Validators.required]],
      postcode: ['',[Validators.required]],
      practiceSoftware: ['', [Validators.required]],
      practiceSoftwareCustom: ['',[Validators.required]],
      practiceManagerName: ['',[Validators.required]],
      practiceManagerEmail: ['', [Validators.email]],
      practiceManagerMobile: ['',[Validators.required]],
      itTechnicalName: [''],
      itTechnicalEmail: [''],
      itTechnicalMobile: [''],
      referralGroupRequesterName: [''],
      referralGroupRequesterEmail: ['', [Validators.email]],
      referralGroupRequesterPhone: ['',[Validators.required]],
      emails: ['', [this.emailsValidator.bind(this)]],
      performer_ids: [[]],
      accountManagerName: [''],
      notes: [''],
    };

    if (this.data.isEdit || this.isStandaloneAgreement) {
      const formConfig: any = {
        ...baseForm,
        referralGroup: [''],
      };

      if ((!this.isStandalonePage && !this.isStandaloneAgreement) || this.isStandaloneAgreement) {
        formConfig.agreeTerms = [false, this.isStandaloneAgreement ? Validators.requiredTrue : []];
        formConfig.agreeReferrals = [false, this.isStandaloneAgreement ? Validators.requiredTrue : []];
      }

      return this.fb.group(formConfig);
    } else {
      const formConfig: any = {
        ...baseForm,
        referralGroup: ['', [Validators.required]]
      };

      if (!this.isStandalonePage) {
        formConfig.agreeTerms = [false, Validators.requiredTrue];
        formConfig.agreeReferrals = [false, Validators.requiredTrue];
      }

      const form = this.fb.group(formConfig);

      if (this.isStandalonePage) {
        form.get('facilityId')?.disable();
      }

      return form;
    }
  }

  populateForm(): void {
    if (this.data.facility) {
      const facility = this.data.facility;
      
      this.facilityForm.patchValue({
        facilityId: facility.FacilityID || '',
        facilityName: facility.ClinicName || '',
        practiceSoftware: facility.practiceSoftware || '',
        emails: facility.medicalClinicEmail || '',
        streetAddress: facility.street_address || '',
        city: facility.city || '',
        state: facility.state || '',
        postcode: facility.postcode || '',
        practiceManagerName: facility.practice_manager_name || '',
        practiceManagerEmail: facility.practice_manager_email || '',
        practiceManagerMobile: facility.practice_manager_mobile || '',
        itTechnicalName: facility.it_technical_name || '',
        itTechnicalEmail: facility.it_technical_email || '',
        itTechnicalMobile: facility.it_technical_mobile || '',
        referralGroupRequesterName: facility.referral_group_requester_name || '',
        referralGroupRequesterEmail: facility.referral_group_requester_email || '',
        referralGroupRequesterPhone: facility.referral_group_requester_phone || '',
        performer_ids: facility.performer_ids,
        notes: facility.notes || '',
        accountManagerName: facility.account_manager_name,
      });

      if (facility.practiceSoftware && 
          !this.practiceSoftwareOptions.some(
            option => option.id.toLowerCase().trim() === facility.practiceSoftware.toLowerCase().trim()
          )
      ) {
        this.facilityForm.patchValue({
          practiceSoftware: 'custom',
          practiceSoftwareCustom: facility.practiceSoftware
        });
      } else if (facility.practiceSoftware === 'custom') {
        this.facilityForm.patchValue({
          practiceSoftwareCustom: facility.practiceSoftware || ''
        });
      }

      if (facility.activeReferralGroupsID && this.data.referralGroups?.length) {
        const referralGroupValue = Number(facility.activeReferralGroupsID);

        const selectedGroup = this.data.referralGroups.find(
          group => group.value === referralGroupValue
        );
        this.selectedReferralGroupName = selectedGroup?.label || '';

        this.facilityForm.patchValue({
          referralGroup: referralGroupValue
        });
      }
    }
  }

  getPracticeSoftwareId(softwareName: string): string | null {
    const mapping: { [key: string]: string } = {
      'Medical Director': 'Medical Director',
      'Best Practice': 'Best Practice'
    };
    return mapping[softwareName] || null;
  }

  cleanClinicName(name: string): string {
    if (!name) return '';
    return name.replace(/\t/g, ' ').replace(/\s+/g, ' ').trim();
  }

  emailsValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null; 

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = control.value.split(',').map((email: string) => email.trim());

    const validEmails = emails.filter((email: string) => email.length > 0);
    
    if (validEmails.length === 0) return null;

    for (const email of validEmails) {
      if (!emailRegex.test(email)) {
        return { invalidEmails: true };
      }
    }

    return null;
  }

  onPracticeSoftwareChange(value: string): void {
    this.facilityForm.patchValue({ practiceSoftware: value });
    
    const customControl = this.facilityForm.get('practiceSoftwareCustom');

    if (value === 'custom') {
      customControl?.setValidators([Validators.required]);
    } else {
      customControl?.clearValidators();
      customControl?.setValue('');
    }

    customControl?.updateValueAndValidity();
  }

  isFormValid(): boolean {
    const requiredFields = [
      'facilityName',
      'practiceSoftware',
      'streetAddress',
      'city',
      'state',
      'postcode',
      'practiceManagerName',
      'practiceManagerEmail',
      'practiceManagerMobile',
      'referralGroupRequesterName',
      'referralGroupRequesterEmail',
      'referralGroupRequesterPhone'
    ];

    const approved = this.data.facility?.approved === true;

    if ( this.data.isEdit && approved ) requiredFields.push('facilityId');

    const isCustomSoftware = this.facilityForm.get('practiceSoftware')?.value === 'custom';
    if (isCustomSoftware) requiredFields.push('practiceSoftwareCustom');

    const allFieldsValid = requiredFields.every(fieldName => {
      const control = this.facilityForm.get(fieldName);
      return control && control.valid && control.value && control.value.toString().trim() !== '';
    });

    return allFieldsValid;
  }

  onSave(): void {
    if (!this.isFormValid()) {
      this.markFormGroupTouched();
      return;
    }

    if (this.data.isEdit && this.data.facility) {
      const { approved, agreements_at } = this.data.facility;
      const facilityId = this.facilityForm.get('facilityId')?.value;
      if (approved && agreements_at && (!facilityId || facilityId.toString().trim() === '') && this.jwtAuthService.isAdmin()) {
        this.alertService.notifyError('Facility ID is required when the facility is approved and the agreement is completed.');
        this.facilityForm.get('facilityId')?.setErrors({ required: true });
        return;
      }
    }

    if (this.requiresOtpVerification()) {
      this.router.navigate([], { 
        fragment: 'otp-verification',
        queryParamsHandling: 'preserve'
      });
    } else {
      this.performActualSave();
    }
  }

  private requiresOtpVerification(): boolean {
    return this.facilityHash !== null && !this.data.isEdit;
  }

  private performActualSave() {
    console.log('Saving facility...', this.facilityForm.value);
    
    if (this.isStandalonePage || this.isStandaloneAgreement) {
      const facilityData = this.facilityForm.value;
      
      let performer_ids = this.facilityForm.get('performer_ids')?.value || [];
      if (this.isAllSelected()) {
        performer_ids = ['*', ...this.performerIdsOptions.map(c => c.id)];
      }
      
      if (this.data.isEdit) {
        const updateData = {
          ...facilityData,
          ID: this.data.facility?.ID || this.data.facility?.FacilityID,
          performer_ids: performer_ids
        };
        this.updateFacilityStandalone(updateData);
      } else {
        facilityData.performer_ids = performer_ids;
        this.createFacilityStandalone(facilityData);
      }
    } else {
      this.dialogRef?.close(this.facilityForm.value);
    }
  }

  private loadFacilityByHash(hash: string): void {
    this.isSubmitting = true;

    this.facilityService.getFacilityByHash(hash).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (response) {
          this.data.facility = response as Facility;
          this.data.isEdit = true;
          
          this.data.id = response.facility.ID;
          this.data.ClinicName = response.facility.ClinicName;
          this.data.FacilityID = response.facility.FacilityID;
          this.data.practiceSoftware = response.facility.practiceSoftware;
          this.data.streetAddress = response.facility.street_address;
          this.data.city = response.facility.city;
          this.data.state = response.facility.state;
          this.data.postcode = response.facility.postcode;
          this.data.practiceManagerName = response.facility.practice_manager_name;
          this.data.practiceManagerEmail = response.facility.practice_manager_email;
          this.data.practiceManagerMobile = response.facility.practice_manager_mobile;
          this.data.itTechnicalName = response.facility.it_technical_name;
          this.data.itTechnicalEmail = response.facility.it_technical_email;
          this.data.itTechnicalMobile = response.facility.it_technical_mobile;
          this.data.referralGroupRequesterName = response.facility.referral_group_requester_name;
          this.data.referralGroupRequesterEmail = response.facility.referral_group_requester_email;
          this.data.referralGroupRequesterPhone = response.facility.referral_group_requester_phone;
          this.agreements = response.facility.agreements || {};

          if (response.facility.activeReferralGroupsID) {
            this.data.userReferralGroupId = parseInt(response.facility.activeReferralGroupsID);

            const selectedGroup = REFERRAL_GROUPS.find(
              group => group.value === parseInt(response.facility.activeReferralGroupsID)
            );

            if (selectedGroup) {
              this.data.userReferralGroupName = selectedGroup.label;
              this.selectedReferralGroupName = selectedGroup.label;
            }
          }

          this.showOtpVerification();
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error loading facility by hash:', error);
        this.alertService.notifyError('Error loading facility data. Please check the link and try again.');
        this.router.navigate(['/admin/facility-management']);
      }
    });
  }

  private showOtpVerification(): void {
    const email = this.data.practiceManagerEmail;
    const facilityId = this.data.id.toString();

    const dialogData: OtpDialogData = {
      title: 'Email Verification Required',
      subtitle: 'Please enter the verification code sent to your email to access the facility agreement',
      type: 'facility',
      email: email,
      id: facilityId
    };

    console.log('OTP Dialog Data (showOtpVerification):', dialogData); // Debug log

    this.otpDialogRef = this.dialog.open(OtpDialogComponent, {
      data: dialogData,
      disableClose: true,
      width: '400px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'otp-dialog-overlay',
      hasBackdrop: true,
      backdropClass: 'otp-backdrop'
    });

    this.otpDialogRef.afterClosed().subscribe((result: boolean) => {
      this.otpDialogRef = null;
      
      if (result === true) {
        this.onOtpVerificationSuccess();
      } else {
        this.onOtpVerificationFailed();
      }
    });
  }

  private onOtpVerificationSuccess(): void {
    const response = this.data.facility;
    if (response) {
      if (response.activeReferralGroupsID && this.data.referralGroups) {
        const selectedGroup = this.data.referralGroups.find(
          group => group.value.toString() === response.activeReferralGroupsID
        );
        this.selectedReferralGroupName = selectedGroup?.label || '';
      }

      this.facilityData = {
        id: this.data.id || '',
        facilityName: this.data.ClinicName || '',
        facilityId: this.data.FacilityID || 'To be assigned',
        streetAddress: this.data.streetAddress || '',
        city: this.data.city || '',
        state: this.data.state || '',
        postcode: this.data.postcode || '',
        practiceSoftware: this.data.practiceSoftware || '',
        practiceManagerName: this.data.practiceManagerName || '',
        practiceManagerEmail: this.data.practiceManagerEmail || '',
        practiceManagerMobile: this.data.practiceManagerMobile || '',
        itTechnicalName: this.data.itTechnicalName || '',
        itTechnicalEmail: this.data.itTechnicalEmail || '',
        itTechnicalMobile: this.data.itTechnicalMobile || '',
        referralGroupRequesterName: this.data.referralGroupRequesterName || '',
        referralGroupRequesterEmail: this.data.referralGroupRequesterEmail || '',
        referralGroupRequesterPhone: this.data.referralGroupRequesterPhone || '',
        referralGroupName: this.selectedReferralGroupName,
        agreements_at: this.data.agreements_at || null
      };

      this.showAgreementView = true;
      this.alertService.notifySuccess('Phone verification successful. Please review and accept the agreements below.');
    }
  }

  private onOtpVerificationFailed(): void {
    this.alertService.notifyError('Phone verification failed. Access denied.');
  }

  onSubmitAgreements(): void {
    if (!this.agreementForm.valid) {
      this.markAgreementFormTouched();
      this.alertService.notifyError('Please accept all agreements to continue.');
      return;
    }

    this.isSubmitting = true;
    
    const submissionData = {
      ...this.facilityData,
      agreements: this.agreementForm.value,
      hash: this.facilityHash
    };

    this.facilityService.acceptAgreementFacility(this.facilityData.id, submissionData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.alertService.notifySuccess('Facility agreement completed successfully!');
        
        if (this.isStandalonePage || this.isStandaloneAgreement) {
          this.router.navigate(['/facility-management-agreement/success']);
        } else {
          this.dialogRef?.close(true);
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error finalizing agreement:', error);
        this.alertService.notifyError('Error completing agreement. Please try again.');
      }
    });
  }

  private markAgreementFormTouched(): void {
    Object.keys(this.agreementForm.controls).forEach(key => {
      const control = this.agreementForm.get(key);
      control?.markAsTouched();
    });
  }

  get allAgreementsAccepted(): boolean {
    return this.agreementForm.valid;
  }

  get dialogTitle(): string {
    if (this.isStandaloneAgreement) {
      return 'Facility Agreement';
    }
    return this.data.isEdit ? 'Edit Facility' : 'Add New Facility';
  }

  get saveButtonText(): string {
    if (this.isStandaloneAgreement) {
      return 'Accept Agreement';
    }
    return this.data.isEdit ? 'Update Facility' : 'Add Facility';
  }

  get showFacilityId(): boolean {
    if (!this.isStandalonePage && this.data.isEdit && this.data.facility && this.data.facility.approved) {
      if (this.jwtAuthService.isAdmin()) {
        return true;
      }
    }
    return false;
  }

  get facilityIdReadonly(): boolean {
    if (this.data.isEdit && this.data.facility) {
      return !this.data.facility.approved || !this.data.facility.agreements_at;
    } else if (!this.data.isEdit && !this.isStandalonePage) return false
    return true;
  }

  get showAgreeCheckboxes(): boolean {
    return (!this.data.isEdit && !this.isStandalonePage) || this.isStandaloneAgreement;
  }

  markFormGroupTouched(): void {
    Object.keys(this.facilityForm.controls).forEach(key => {
      const control = this.facilityForm.get(key);
      control?.markAsTouched();
    });
  }

  getErrorMessage(field: string): string {
    const control = this.facilityForm.get(field);

    if (control?.hasError('required')) {
      return `${this.getFieldLabel(field)} is required`;
    }

    if (control?.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control?.hasError('pattern')) {
      if (field.includes('Mobile') || field.includes('Phone')) {
        return 'Please enter a valid Australian phone number';
      }
      if (field === 'postcode') {
        return 'Please enter a valid 4-digit postcode';
      }
    }

    if (field === 'emails' && control?.hasError('invalidEmails')) {
      return 'Please enter valid email addresses separated by commas';
    }

    return '';
  }

  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      facilityId: 'Facility ID',
      facilityName: 'Facility Name',
      referralGroup: 'Referral Group',
      practiceSoftware: 'Practice Software',
      practiceSoftwareCustom: 'Custom Software',
      emails: 'Clinic Emails',
      streetAddress: 'Street Address',
      city: 'City',
      state: 'State',
      postcode: 'Postcode',
      practiceManagerName: 'Practice Manager Name',
      practiceManagerEmail: 'Practice Manager Email',
      practiceManagerMobile: 'Practice Manager Mobile',
      itTechnicalName: 'IT Technical Person Name',
      itTechnicalEmail: 'IT Technical Person Email',
      itTechnicalMobile: 'IT Technical Person Mobile',
      referralGroupRequesterName: 'Referral Group Requester Name',
      referralGroupRequesterEmail: 'Referral Group Requester Email',
      referralGroupRequesterPhone: 'Referral Group Requester Phone'
    };

    return labels[fieldName] || fieldName;
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  get isCustomSoftware(): boolean {
    return this.facilityForm.get('practiceSoftware')?.value === 'custom';
  }

  private createFacilityStandalone(facilityData: any): void {
    this.isSubmitting = true;

    this.facilityService.createFacility(facilityData).subscribe({
      next: response => {
        this.isSubmitting = false;

        let successMsg = response.message || 'Facility created successfully';

        if (response.email_error && response.email_error.length > 0) {
          successMsg += `. However, some emails failed: ${response.email_error.join(', ')}`;
        }

        this.alertService.notifySuccess(successMsg);
        this.facilityForm.reset();
      },
      error: error => {
        this.isSubmitting = false;
        console.error('Error creating facility:', error);

        if (error.error?.message) {
          this.alertService.notifyError(`Error creating facility: ${error.error.message}`);
        } else if (error.error?.errors) {
          const errors = error.error.errors;
          const errorMessages: string[] = [];

          for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
              const value = errors[key];
              if (Array.isArray(value)) {
                errorMessages.push(...value);
              } else if (typeof value === 'string') {
                errorMessages.push(value);
              }
            }
          }

          this.alertService.notifyError(`Validation errors: ${errorMessages.join(', ')}`);
        } else {
          this.alertService.notifyError('Error creating facility. Please try again.');
        }
      }
    });
  }

  private updateFacilityStandalone(facilityData: any): void {
    this.isSubmitting = true;

    this.facilityService.updateFacility(facilityData.ID, facilityData).subscribe({
      next: response => {
        this.isSubmitting = false;
        this.alertService.notifySuccess('Facility updated successfully');
      },
      error: error => {
        this.isSubmitting = false;
        console.error('Error updating facility:', error);
        this.alertService.notifyError('Error updating facility. Please try again.');
      }
    });
  }

  loadPerformerIds(referralGroupId: number, page: number, per_page: number = 80): void {
    if (this.isLoadingPerformerIds) return;
    this.isLoadingPerformerIds = true;

    this.facilityService.getReferralPerformers({ referral_group_id: referralGroupId, for_drop_down: 1 }).subscribe({
      next: (res) => {
        if (res?.data) {
          this.performerIdsOptions = res.data;
          this.performerIdsTotal = res.total;
        }
        this.isLoadingPerformerIds = false;
      },
      error: () => {
        this.isLoadingPerformerIds = false;
      }
    });
  }

  isAllSelected(): boolean {
    const selected = this.facilityForm.get('performer_ids')?.value || [];
    return selected.length === this.performerIdsOptions.length && this.performerIdsOptions.length > 0;
  }

  toggleSelectAll(): void {
    const allIds = this.performerIdsOptions.map(c => c.id);
    if (this.isAllSelected()) {
      this.facilityForm.get('performer_ids')?.setValue([]);
    } else {
      this.facilityForm.get('performer_ids')?.setValue(allIds);
    }
  }
}